// DataSourceRowActions — the listing's kebab cell, carrying the same three
// actions the selection bar offers: Edit · View file entries · Delete. Here they
// always act on this one row, so there is no single/multi split — that only
// applies to the bar.
//
// All three are live; Edit reopens the create modal seeded from this row (PATCH
// datasource/:id takes the create body unchanged).
//
// Owns its own open state (one row's menu at a time) and stops clicks bubbling
// into the row, which navigates to this source's entries.

import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';

interface DataSourceRowActionsProps {
  /** Opens the create modal in edit mode, seeded from this row. Absent = hidden. */
  onEdit?: () => void;
  /** Opens this source's entries — the same target a row click has. Always
   *  present: the permanent read item, so the kebab never empties. */
  onViewEntries: () => void;
  /** Opens the page's delete confirmation for this one row. Absent = hidden. */
  onDelete?: () => void;
}

const DataSourceRowActions = ({ onEdit, onViewEntries, onDelete }: DataSourceRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const stop = (e: MouseEvent) => e.stopPropagation();

  // Every item closes the menu first, so the page's modal or navigation isn't
  // left rendering underneath an open dropdown.
  const pick = (run: () => void) => (e: MouseEvent) => {
    stop(e);
    setOpen(false);
    run();
  };

  return (
    <span onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='end'
        portal
        width={220}
        trigger={
          <IconButton
            icon='ellipsis-vertical'
            variant='ghost'
            size='sm'
            aria-label={t('datasource_row_actions')}
            onClick={(e) => {
              stop(e);
              setOpen((o) => !o);
            }}
          />
        }
      >
        {onEdit && (
          <button
            type='button'
            role='menuitem'
            className='app-dropdown__item'
            onClick={pick(onEdit)}
          >
            <Icon name='square-pen' size={16} />
            <span className='app-dropdown__item-label'>{t('datasource_action_edit')}</span>
          </button>
        )}
        <button
          type='button'
          role='menuitem'
          className='app-dropdown__item'
          onClick={pick(onViewEntries)}
        >
          <Icon name='table-2' size={16} />
          <span className='app-dropdown__item-label'>{t('datasource_action_view_entries')}</span>
        </button>
        {onDelete && (
          <button
            type='button'
            role='menuitem'
            className='app-dropdown__item app-dropdown__item--danger'
            onClick={pick(onDelete)}
          >
            <Icon name='trash-2' size={16} />
            <span className='app-dropdown__item-label'>{t('button_delete')}</span>
          </button>
        )}
      </Dropdown>
    </span>
  );
};

export default DataSourceRowActions;
